/**
 * src/components/agent/EmptyStateSuggestions.jsx — Context-aware empty state.
 *
 * Phase 82-06 — Chat System Unification (wave 5).
 *
 * Renders when `chat.messages.length === 0`. Picks 4-6 starter cards from
 * suggestionsByContext[ctx], interpolates the scope entity name into each
 * template, and dispatches the click:
 *   - `suggestion.mode === 'instant'` → onInstantSend(renderedText)
 *   - otherwise                        → onFillInput(renderedText)
 *
 * Card grid: 1 column on mobile, 2 columns on tablet/desktop.
 * Design tokens only. 44px min touch per card.
 */

import { useCallback, useMemo } from 'react';
import SuggestionCard from './SuggestionCard';
import {
  getSuggestions,
  renderTemplate,
  getHeadline,
} from '../../lib/agent/suggestionsByContext';
import { useT } from '../../lib/i18n/I18nContext';

const rootStyle = {
  width: '100%',
  maxWidth: 880,
  margin: '0 auto',
  padding: '24px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const headlineStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--navy)',
  margin: 0,
  lineHeight: 1.3,
};

const subheadStyle = {
  fontSize: 13,
  color: 'var(--tm)',
  margin: 0,
  lineHeight: 1.4,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 12,
};

export default function EmptyStateSuggestions({
  ctx,
  scope,
  onFillInput,
  onInstantSend,
}) {
  const { t } = useT();
  const suggestions = useMemo(() => getSuggestions(ctx), [ctx]);
  const headline = useMemo(() => getHeadline(ctx, scope), [ctx, scope]);

  const handleChoose = useCallback(
    suggestion => {
      if (!suggestion) return;
      const text = renderTemplate(suggestion.template, scope);
      if (suggestion.mode === 'instant') {
        if (typeof onInstantSend === 'function') onInstantSend(text);
      } else if (typeof onFillInput === 'function') {
        onFillInput(text);
      }
    },
    [scope, onFillInput, onInstantSend],
  );

  return (
    <section
      style={rootStyle}
      data-testid="agent-empty-state"
      data-ctx={ctx || 'global'}
      aria-label={headline}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 style={headlineStyle} data-testid="agent-empty-state-headline">
          {headline}
        </h2>
        <p style={subheadStyle}>{t('agentChat.emptyStateHint')}</p>
      </header>
      <div style={gridStyle} role="list">
        {suggestions.map(s => (
          <div role="listitem" key={s.id}>
            <SuggestionCard suggestion={s} onChoose={handleChoose} />
          </div>
        ))}
      </div>
    </section>
  );
}
